<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  captainDocument: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const dialogRef = ref(null);

const content = computed(() => props.captainDocument?.content || '');
const characterCount = computed(() => content.value.length);

const handleCopy = async () => {
  try {
    await navigator.clipboard.writeText(content.value);
    useAlert(t('CAPTAIN.DOCUMENTS.CONTENT_DIALOG.COPIED'));
  } catch (error) {
    useAlert(t('CAPTAIN.DOCUMENTS.CONTENT_DIALOG.COPY_ERROR'));
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="captainDocument?.name || ''"
    :show-confirm-button="false"
    :cancel-button-label="$t('CAPTAIN.DOCUMENTS.CONTENT_DIALOG.CLOSE')"
    width="2xl"
  >
    <div class="flex flex-col gap-3">
      <div class="flex gap-3 justify-between items-center">
        <span class="text-sm text-n-slate-11">
          {{
            $t('CAPTAIN.DOCUMENTS.CONTENT_DIALOG.CHARACTERS', {
              count: characterCount,
            })
          }}
        </span>
        <Button
          :label="$t('CAPTAIN.DOCUMENTS.CONTENT_DIALOG.COPY')"
          icon="i-lucide-copy"
          variant="ghost"
          color="slate"
          size="sm"
          @click="handleCopy"
        />
      </div>

      <!-- Plain text, rendered as-is rather than as markdown: showing exactly
           what the assistant indexed is the whole point of this dialog.
           A div with whitespace-pre-wrap instead of <pre>, so the formatter can
           break the tag freely without the line breaks becoming real
           whitespace in the output. -->
      <div
        class="overflow-auto p-3 max-h-[60vh] text-sm break-words whitespace-pre-wrap rounded-lg text-n-slate-12 bg-n-alpha-1"
      >
        {{ content }}
      </div>
    </div>
  </Dialog>
</template>
